import React, { useMemo } from "react";
import {
  formatCurrencyPrice,
  getUserTypeLabel,
  isNullOrUndefined
} from "./helpers";
import { PriceProps } from "./types";
import RHLink from "@RHCommerceDev/component-rh-link";
import { cn } from "@RHCommerceDev/utils/cn";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import { INCLUDES_ECO_PARTICIPATION } from "@RHCommerceDev/resources/page-level-resources-schemas/product.json";
import { FR } from "@RHCommerceDev/utils/constants";
import yn from "yn";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { countryCodeMapper } from "@RHCommerceDev/resources/countries-config.json";
import EcoParticipation from "@RHCommerceDev/component-eco-participation";
import { processEnvServer } from "hooks/useSsrHooks";

const RHRPriceDisplay = (props: PriceProps) => {
  const {
    productName,
    listPrice,
    memberPrice,
    salePrice,
    topLabel,
    isSaleFilterEnabled,
    onSale,
    skulowestMemberPrice,
    showSaleMessage,
    userType,
    pageContent,
    computedSalePageContent,
    wrapPrices = false,
    centerAlignFlag = false,
    variant,
    showGiftCardPrice,
    showMembershipProductPrice,
    isCustomProduct,
    isSkuLevel,
    source,
    hideCss,
    onSaleLinkClick,
    saleUrl,
    metaProduct,
    emptyProduct,
    showInline = false,
    classes = {},
    hideSeparator = false,
    ecoFee
  } = props;

  const displayPriceComponent = listPrice && memberPrice;
  const country = getCountryFromUrl();
  const shippinCountryFromLocalStorage = !processEnvServer
    ? localStorage?.getItem("shippingCountry")
    : undefined;
  const env = useEnv();
  const FEATURE_FRANCE_288_ECO_FEE = yn(env.FEATURE_FRANCE_288_ECO_FEE);
  const ecoFeeEnabled =
    FEATURE_FRANCE_288_ECO_FEE &&
    country === FR &&
    shippinCountryFromLocalStorage !== countryCodeMapper["MC"] &&
    (ecoFee ?? 0) > 0;

  if (isCustomProduct || !displayPriceComponent) return <></>;

  const isOnSale =
    isSaleFilterEnabled && onSale ? true : onSale && !showSaleMessage;

  const checkSale = showInline && !isOnSale;
  const onSaleLinkAriaLabel = ` ${computedSalePageContent}  ${
    productName ?? ""
  }`;

  const userTypeLabel = useMemo(() => getUserTypeLabel(userType!), [userType]);

  const showViewSelectItemOnSale =
    showSaleMessage &&
    !isOnSale &&
    !showGiftCardPrice &&
    !showMembershipProductPrice &&
    !isCustomProduct &&
    !(!metaProduct && emptyProduct);

  const checkMemberShipGiftCard =
    !showGiftCardPrice && !showMembershipProductPrice;

  const memberPriceVal = useMemo(() => {
    return isOnSale ? skulowestMemberPrice : memberPrice;
  }, [isOnSale, memberPrice, skulowestMemberPrice]);

  const computedEcoFee = formatCurrencyPrice(ecoFee);
  const FEATURE_SALE_PRICE = yn(env.FEATURE_SALE_PRICE);

  return (
    <>
      {!FEATURE_SALE_PRICE ? (
        <div
          className={cn(
            `box-border flex w-full flex-col justify-center pr-2.5 xs:justify-start`,
            {
              "items-center": centerAlignFlag,
              "items-start": !centerAlignFlag
            },
            classes?.container
          )}
        >
          <div
            className={cn(
              `flex flex-row flex-wrap items-baseline leading-[19.5px] tracking-[0.195px] text-rhBlack`,
              {
                "justify-center xs:items-center": !hideCss && centerAlignFlag,
                "whitespace-nowrap": !wrapPrices && source !== "spo",
                "text-center": wrapPrices && centerAlignFlag,
                "justify-left": hideCss && !centerAlignFlag,
                "text-[13px]": variant === "medium",
                "text-[11px]": !variant || variant !== "medium"
              }
            )}
          >
            {topLabel && !showMembershipProductPrice ? (
              <div className={classes?.topLabel || ""}>
                {isOnSale && !isSkuLevel && (
                  <span
                    className={cn(`my-0 mr-1 uppercase`, {
                      "font-primary-rhroman text-[13px] text-[#CA2022]":
                        isOnSale,
                      "font-primary-rhthin text-[13px] text-[#000]": !isOnSale
                    })}
                    id="RHPriceDisplaySale_sale_label"
                  >
                    {pageContent?.sale ? pageContent?.sale : pageContent?.SALE}
                  </span>
                )}
                <span
                  className={cn("my-0 mr-1 text-[13px]", {
                    "text-[#CA2022]": isOnSale || onSale,
                    "font-rhsans font-thin text-[#000]": !onSale,
                    "whitespace-nowrap": hideCss && source === "spo"
                  })}
                  id="RHPriceDisplaySale_sale_top_label"
                >
                  {topLabel}
                </span>
              </div>
            ) : null}
            {!isNullOrUndefined(memberPriceVal) &&
              checkMemberShipGiftCard &&
              showInline && (
                <>
                  <span
                    className={cn(
                      `${
                        source === "spo"
                          ? "leading-[19.5px] tracking-[0.195px]"
                          : "tracking-[0.195px]"
                      } mr-1`,
                      {
                        "font-primary-rhroman text-[13px] text-[#CA2022]":
                          isOnSale || onSale,
                        "font-primary-rhthin text-[13px] text-[#000]": !onSale
                      }
                    )}
                    id="RHPriceDisplaySale_member_price_inline"
                  >
                    {`${formatCurrencyPrice(memberPriceVal)}`}
                  </span>
                  {checkSale && (
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : "tracking-[0.195px]"
                        } mr-1 font-primary-rhroman`,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-primary-rhthin text-[13px] text-[#000]":
                            !onSale,
                          "whitespace-nowrap": hideCss && source === "spo"
                        }
                      )}
                      id={`RHPriceDisplaySale_${
                        pageContent?.[userTypeLabel?.toUpperCase()]
                      }_price_label_inline`}
                    >
                      {pageContent?.[userTypeLabel?.toUpperCase()]}
                      {" /"}
                    </span>
                  )}
                </>
              )}
            <span
              id="RHPriceDisplaySale_Member_price_wrapper"
              className={`${classes?.memberPriceWraapper}`}
            >
              {!isNullOrUndefined(memberPriceVal) &&
                checkMemberShipGiftCard &&
                !showInline && (
                  <>
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : "tracking-[0.195px]"
                        } mr-1`,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-rhsans text-[13px] font-thin text-[#000]":
                            !onSale
                        },
                        classes?.memberPrice
                      )}
                      id="RHPriceDisplaySale_member_price"
                    >
                      {formatCurrencyPrice(memberPriceVal)}
                    </span>
                  </>
                )}
              {!isNullOrUndefined(memberPriceVal) &&
                checkMemberShipGiftCard &&
                !checkSale && (
                  <>
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : " tracking-[0.195px]"
                        } mr-1 `,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-rhsans text-[13px] font-thin text-[#000]":
                            !onSale
                        },
                        classes?.memberPrice
                      )}
                      id={`RHPriceDisplaySale_${
                        pageContent?.[userTypeLabel?.toUpperCase()]
                      }_price_label`}
                    >
                      {`${pageContent?.[userTypeLabel?.toUpperCase()]}${
                        hideSeparator ? "" : " /"
                      }`}
                    </span>
                  </>
                )}
            </span>
            {!isNullOrUndefined(listPrice) && (
              <span>
                <span
                  className={cn(
                    `${
                      source === "spo"
                        ? "leading-[19.5px] tracking-[0.195px]"
                        : "tracking-[0.195px]"
                    } mr-1 font-rhsans text-[13px] font-thin text-[#000]`,
                    classes?.listPrice
                  )}
                  id="RHPriceDisplaySale_list_price"
                >
                  {formatCurrencyPrice(listPrice)}
                </span>
                {checkMemberShipGiftCard && pageContent?.REGULAR && (
                  <span
                    className={cn(
                      `${
                        source === "spo"
                          ? "leading-[19.5px] tracking-[0.195px]"
                          : "tracking-[0.195px]"
                      } font-rhsans text-[13px] font-thin text-[#000]`,
                      classes?.listPrice
                    )}
                    id="RHPriceDisplaySale_regular_price_label"
                  >
                    {pageContent?.REGULAR}
                  </span>
                )}
              </span>
            )}
          </div>

          {ecoFeeEnabled && (
            <EcoParticipation
              ecofee={computedEcoFee}
              ecoFeeText={INCLUDES_ECO_PARTICIPATION}
              classNames="tracking-[0.02rem]"
            />
          )}
          {showViewSelectItemOnSale && (
            <>
              <RHLink
                aria-label={onSaleLinkAriaLabel.toLowerCase()}
                underline="none"
                to={saleUrl && saleUrl}
                onClick={() => {
                  onSaleLinkClick && onSaleLinkClick();
                }}
                tabIndex={0}
              >
                <div
                  className={cn(
                    `box-border flex w-full flex-wrap items-baseline`,
                    {
                      "justify-center text-center": centerAlignFlag
                    }
                  )}
                  style={{ letterSpacing: "0.20px" }}
                >
                  <div
                    className={cn(
                      `font-primary-rhroman font-normal leading-[2rem] text-[#CA2022]`,
                      {
                        underline: onSaleLinkClick || saleUrl,
                        "text-[13px]": variant === "medium",
                        "text-[11px]": variant === "small",
                        "text-[25px]": variant === "print"
                      },
                      classes?.saleMessage
                    )}
                  >
                    {computedSalePageContent}
                  </div>
                </div>
              </RHLink>
            </>
          )}
        </div>
      ) : (
        <div
          className={cn(
            `box-border flex w-full flex-col justify-center pr-2.5 `,
            {
              "items-center": centerAlignFlag,
              "items-start": !centerAlignFlag
            },
            classes?.container
          )}
        >
          <div
            className={cn(
              `flex flex-row flex-wrap items-baseline leading-[18px] tracking-[0.165px] text-rhBlack`,
              {
                "justify-center xs:items-center": centerAlignFlag,
                "text-center": wrapPrices && centerAlignFlag,
                "justify-left": !centerAlignFlag,
                "text-[13px]": variant === "medium",
                "text-[11px]": !variant || variant !== "medium"
              }
            )}
          >
            {/* Starting At line - can be with Member Sale or by itself */}
            {topLabel && !showMembershipProductPrice && (
              <div
                className={cn(
                  "flex flex-wrap items-baseline",
                  classes?.topLabel || ""
                )}
              >
                <span
                  className={cn("my-0 mr-1", {
                    "font-primary-rhroman text-[13px] text-[#CA2022]":
                      isOnSale || onSale,
                    "font-primary-rhthin text-[13px] text-[#000]": !onSale
                  })}
                  id="RHPriceDisplaySale_sale_top_label"
                >
                  {topLabel}
                </span>
              </div>
            )}
            <span
              id="RHPriceDisplaySale_Member_price_wrapper"
              className={`${classes?.memberPriceWraapper}`}
            >
              {!isNullOrUndefined(memberPriceVal) &&
                checkMemberShipGiftCard &&
                showInline && (
                  <>
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : "tracking-[0.195px]"
                        } mr-1`,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-primary-rhthin text-[13px] text-[#000]": !onSale
                        }
                      )}
                      id="RHPriceDisplaySale_member_price_inline"
                    >
                      {`${formatCurrencyPrice(memberPriceVal)}`}
                    </span>
                    {checkSale && (
                      <span
                        className={cn(
                          `${
                            source === "spo"
                              ? "leading-[19.5px] tracking-[0.195px]"
                              : "tracking-[0.195px]"
                          } mr-1 `,
                          {
                            "font-primary-rhroman text-[13px] text-[#CA2022]":
                              isOnSale || onSale,
                            "font-primary-rhthin text-[13px] text-[#000]":
                              !onSale,
                            "whitespace-nowrap": hideCss && source === "spo"
                          }
                        )}
                        id={`RHPriceDisplaySale_${
                          pageContent?.[userTypeLabel?.toUpperCase()]
                        }_price_label_inline`}
                      >
                        {`${
                          isOnSale || onSale
                            ? pageContent?.[
                                `${userTypeLabel.toUpperCase()}_SALE_PRICE_TAG`
                              ]
                            : pageContent?.[userTypeLabel?.toUpperCase()]
                        } /`}
                      </span>
                    )}
                  </>
                )}
              {!isNullOrUndefined(memberPriceVal) &&
                checkMemberShipGiftCard &&
                !showInline && (
                  <>
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : "tracking-[0.195px]"
                        } mr-1`,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-primary-rhthin text-[13px] text-[#000]": !onSale
                        },
                        classes?.memberPrice
                      )}
                      id="RHPriceDisplaySale_member_price"
                    >
                      {formatCurrencyPrice(memberPriceVal)}
                    </span>
                  </>
                )}
              {!isNullOrUndefined(memberPriceVal) &&
                checkMemberShipGiftCard &&
                !checkSale && (
                  <>
                    <span
                      className={cn(
                        `${
                          source === "spo"
                            ? "leading-[19.5px] tracking-[0.195px]"
                            : "tracking-[0.195px]"
                        } mr-1`,
                        {
                          "font-primary-rhroman text-[13px] text-[#CA2022]":
                            isOnSale || onSale,
                          "font-primary-rhthin text-[13px] text-[#000]": !onSale
                        },
                        classes?.memberPrice
                      )}
                      id={`RHPriceDisplaySale_${
                        pageContent?.[userTypeLabel?.toUpperCase()]
                      }_price_label`}
                    >
                      {`${
                        isOnSale || onSale
                          ? pageContent?.[
                              `${userTypeLabel.toUpperCase()}_SALE_PRICE_TAG`
                            ]
                          : pageContent?.[userTypeLabel?.toUpperCase()]
                      }${hideSeparator ? "" : " /"}`}
                    </span>
                  </>
                )}
            </span>
            {!!salePrice && onSale && (
              <span className="font-primary-rhthin text-[13px] text-[#CA2022]">
                {formatCurrencyPrice(salePrice)}
                {` ${
                  pageContent?.sale ? pageContent?.sale : pageContent?.SALE
                } /\u00A0`}
              </span>
            )}
            {!isNullOrUndefined(listPrice) && (
              <span>
                <span
                  className={cn(
                    `${
                      source === "spo"
                        ? "leading-[19.5px] tracking-[0.195px]"
                        : "tracking-[0.195px]"
                    } mr-1 font-primary-rhthin text-[13px] text-[#000]`,
                    classes?.listPrice
                  )}
                  id="RHPriceDisplaySale_list_price"
                >
                  {formatCurrencyPrice(listPrice)}
                </span>
                {checkMemberShipGiftCard && pageContent?.REGULAR && (
                  <span
                    className={cn(
                      `${
                        source === "spo"
                          ? "leading-[19.5px] tracking-[0.195px]"
                          : "tracking-[0.195px]"
                      } font-primary-rhthin text-[13px] text-[#000]`,
                      classes?.listPrice
                    )}
                    id="RHPriceDisplaySale_regular_price_label"
                  >
                    {pageContent?.REGULAR}
                  </span>
                )}
              </span>
            )}
          </div>

          {ecoFeeEnabled && (
            <EcoParticipation
              ecofee={computedEcoFee}
              ecoFeeText={INCLUDES_ECO_PARTICIPATION}
              classNames="tracking-[0.02rem]"
            />
          )}
          {showViewSelectItemOnSale && (
            <>
              <RHLink
                aria-label={onSaleLinkAriaLabel.toLowerCase()}
                underline="none"
                to={saleUrl && saleUrl}
                onClick={() => {
                  onSaleLinkClick && onSaleLinkClick();
                }}
                tabIndex={0}
              >
                <div
                  className={cn(
                    `box-border flex w-full flex-wrap items-baseline`,
                    {
                      "justify-center text-center": centerAlignFlag
                    }
                  )}
                  style={{ letterSpacing: "0.20px" }}
                >
                  <div
                    className={cn(
                      `font-primary-rhroman font-normal leading-[2rem] text-[#CA2022]`,
                      {
                        underline: onSaleLinkClick || saleUrl,
                        "text-[13px]": variant === "medium",
                        "text-[11px]": variant === "small",
                        "text-[25px]": variant === "print"
                      },
                      classes?.saleMessage
                    )}
                  >
                    {computedSalePageContent}
                  </div>
                </div>
              </RHLink>
            </>
          )}
        </div>
      )}
    </>
  );
};
export default React.memo(RHRPriceDisplay);
